import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ReadyState } from 'react-use-websocket'
import { v4 as uuidv4 } from 'uuid'

import { useSnackbar } from '@context/SnackbarContext'

import { FIX_ORDER_TYPE } from '@model/DxTrade.types'
import { CurrencyType } from '@model/WalletDto'

import { useWebSocket } from '@hooks/useWebSocket'

import { FIX_POSITION_EFFECT } from '../../utils/constants'
import { getFixTradingErrorMessage, getGenericExceptionMessage } from '../utils/fixTradingErrors'
import { formatSignedUsd } from '../utils/formatters'
import { calculatePnL } from '../utils/functions'
import { sideReverseMapping } from '../utils/mapping'

export type FixTradingMessage = { [key: string]: unknown }

export type UseFixTradingWebSocketOptions = {
  onMessage?: (message: FixTradingMessage) => void
}

export type CloseAllPositionsParams = {
  accountCode: string
  positionIds: string[]
  onSuccess?: () => void
  onSettled?: () => void
  symbol?: string
}

export type UseFixTradingWebSocketResult = {
  readyState: ReadyState
  cancelOrder: (params: OrderCancelParams) => string
  newOrder: (params: NewOrderParams, onSuccess?: () => void, onSettled?: () => void) => string
  newOrderList: (
    params: NewOrderListParams,
    onSuccess?: () => void,
    onSettled?: () => void
  ) => string
  changePendingOrderSlTp: (
    params: ChangePendingOrderSlTpParams,
    onSuccess?: () => void,
    onSettled?: () => void
  ) => string
  replaceOrder: (
    params: OrderCancelReplaceParams,
    onSuccess?: () => void,
    onSettled?: () => void
  ) => string
  closePosition: (params: ClosePositionOrderParams, context?: CloseContext) => string
  closeAllPositions: (params: CloseAllPositionsParams) => string
}

const FIX_TRADING_TYPES = {
  ORDER_CANCEL_REQUEST: 'OrderCancelRequest',
  ORDER_CANCEL_REPLACE_REQUEST: 'OrderCancelReplaceRequest',
  NEW_ORDER_SINGLE: 'NewOrderSingle',
  NEW_ORDER_LIST: 'NewOrderList',
  CHANGE_PENDING_ORDER_SL_TP: 'ChangePendingOrderSlTpRequest',
  CLOSE_ALL_ORDERS: 'CloseAllOrders',
  CLOSE_SYMBOL_ORDERS: 'CloseSymbolOrders',
} as const

const DEFAULT_FIXTRADING_PATH = 'platform-fixtrading'
const ORDER_ID_PREFIX = 'CA_'
const REQUEST_TIMEOUT_MS = 60000

type ExecType = '0' | '2' | '3' | '4' | '5' | '8' | 'A' | 'C' | 'E' | 'F' | 'I'
const EXEC_TYPE = {
  NEW: '0',
  FILL: '2',
  DONE_FOR_DAY: '3',
  CANCELLED: '4',
  REPLACE: '5',
  REJECTED: '8',
  PENDING_NEW: 'A',
  EXPIRED: 'C',
  PENDING_REPLACE: 'E',
  TRADE: 'F',
  ORDER_STATUS: 'I',
} as const

export type OrderCancelParams = {
  origClOrdID: string
  account: string
  listId: string
  symbol: string
  side: '1' | '2'
  id?: string
  isTpsl?: boolean
  onSuccess?: () => void
  onSettled?: () => void
  suppressSuccessMessage?: boolean
}

export const FixContingencyTypes = {
  OCO: '1',
  IF_THEN: '101',
} as const

export type FixContingencyType = (typeof FixContingencyTypes)[keyof typeof FixContingencyTypes]

type BaseNewOrderParams = {
  account: string
  symbol: string
  side: '1' | '2'
  orderQty?: number
  ordType: string
  positionEffect?: string
  timeInForce?: string
  expireDate?: string
  positionID?: string
  price?: number | null
  isTpsl?: boolean
  isExpiryUpdate?: boolean
}

export type NewOrderListOrderParams = BaseNewOrderParams & {
  clOrdId?: string
}

export type NewOrderListParams = {
  listId?: string
  contingencyType: FixContingencyType
  orders: NewOrderListOrderParams[]
  isTpsl?: boolean
}

export type ChangePendingOrderSlTpParams = {
  origClOrdID: string
  account: string
  symbol: string
  side: '1' | '2'
  contingencyType: FixContingencyType
  orders: NewOrderListOrderParams[]
}

export type OrderCancelReplaceParams = BaseNewOrderParams & {
  origClOrdID: string
  listId?: string
}

export type ClosePositionOrderParams = BaseNewOrderParams & {
  positionEffect: typeof FIX_POSITION_EFFECT.CLOSE
  positionID: string
  ordType: '1'
  price?: never
}

export type NewOrderParams = BaseNewOrderParams

export function useFixTradingWebSocket(
  options?: UseFixTradingWebSocketOptions
): UseFixTradingWebSocketResult {
  const { onMessage } = options || {}
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()

  const requestsByIdRef = useRef<Map<string, OriginInfo>>(new Map<string, OriginInfo>())
  const requestTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const closeAllStateRef = useRef<CloseAllState | null>(null)

  const { readyState, sendMessage } = useWebSocket(DEFAULT_FIXTRADING_PATH, {
    heartbeat: false,
    onMessage: (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
        processFixMessage(data, {
          requestsById: requestsByIdRef.current,
          addSnackbar,
          t,
          requestTimeouts: requestTimeoutsRef.current,
          closeAllState: closeAllStateRef.current,
          clearCloseAllState: () => {
            closeAllStateRef.current = null
          },
        })
        onMessage?.(data)
      } catch {
        onMessage?.(event.data)
      }
    },
  })

  useEffect(() => {
    return () => {
      requestTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId))
      requestTimeoutsRef.current.clear()

      if (closeAllStateRef.current?.timeoutId) {
        clearTimeout(closeAllStateRef.current.timeoutId)
        closeAllStateRef.current = null
      }
    }
  }, [])

  const setRequestTimeout = (
    id: string,
    timeoutMs: number,
    errorMessage: string,
    onTimeout?: () => void
  ) => {
    const timeoutId = setTimeout(() => {
      requestTimeoutsRef.current.delete(id)
      const timedOutInfo = requestsByIdRef.current.get(id)
      timedOutInfo?.onSettled?.()
      requestsByIdRef.current.delete(id)
      addSnackbar.error({ message: errorMessage })
      onTimeout?.()
    }, timeoutMs)
    requestTimeoutsRef.current.set(id, timeoutId)
  }

  const cancelOrder = (params: OrderCancelParams): string => {
    if (readyState !== ReadyState.OPEN) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      params.onSettled?.()
      return ''
    }
    const id = `${ORDER_ID_PREFIX}${uuidv4()}`
    const payload = {
      $type: FIX_TRADING_TYPES.ORDER_CANCEL_REQUEST,
      Id: id,
      ClOrdID: id,
      OrigClOrdID: params.origClOrdID,
      ListID: params.listId,
      Account: params.account,
      Symbol: params.symbol,
      Side: params.side,
    }
    try {
      requestsByIdRef.current.set(id, {
        origin: params.isTpsl ? ORIGIN.TPSL : ORIGIN.CANCEL_ORDER,
        onSuccess: params.onSuccess,
        onSettled: params.onSettled,
        suppressSuccessMessage: params.suppressSuccessMessage,
      })
      sendMessage(JSON.stringify(payload))
      setRequestTimeout(
        id,
        REQUEST_TIMEOUT_MS,
        t('Portfolio.Unable to cancel the order. Please try again')
      )
    } catch {
      requestsByIdRef.current.delete(id)
      addSnackbar.error({ message: t('Portfolio.Unable to cancel the order. Please try again') })
    }
    return id
  }

  const newOrderList = (
    params: NewOrderListParams,
    onSuccess?: () => void,
    onSettled?: () => void
  ): string => {
    if (readyState !== ReadyState.OPEN) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      onSettled?.()
      return ''
    }

    const listId = `${ORDER_ID_PREFIX}${uuidv4()}`
    const clOrdIds: string[] = []

    const payload: Record<string, unknown> = {
      $type: FIX_TRADING_TYPES.NEW_ORDER_LIST,
      ListID: listId,
      ContingencyType: params.contingencyType,
      TotNoOrders: params.orders.length,
      NoOrders: params.orders.length,
    }

    const ordersPayload = params.orders.map((order, index) => {
      const clOrdId = `${ORDER_ID_PREFIX}${uuidv4()}`
      clOrdIds.push(clOrdId)

      const orderPayload: Record<string, unknown> = {
        ClOrdID: clOrdId,
        Account: order.account,
        Symbol: order.symbol,
        Side: order.side,
        OrdType: order.ordType,
      }

      if (order.positionEffect === FIX_POSITION_EFFECT.CLOSE) {
        orderPayload.OrderQty = 0
      } else if (order.orderQty !== undefined) {
        orderPayload.OrderQty = order.orderQty
      }
      if (order.positionEffect !== undefined) orderPayload.PositionEffect = order.positionEffect
      if (order.timeInForce !== undefined) orderPayload.TimeInForce = order.timeInForce
      if (order.expireDate !== undefined) orderPayload.ExpireDate = order.expireDate
      if (order.positionID !== undefined) orderPayload.PositionID = order.positionID

      applyPrice(orderPayload, order.price, order.ordType)

      const isPrimary = index === 0
      requestsByIdRef.current.set(clOrdId, {
        origin: params.isTpsl ? ORIGIN.TPSL : ORIGIN.NEW,
        onSuccess: isPrimary ? onSuccess : undefined,
        onSettled: isPrimary ? onSettled : undefined,
        isListPrimary: isPrimary,
        listGroupId: listId,
        requiresTradeConfirmation: isPrimary ? requiresTradeConfirmationForNewOrder(order) : false,
      })

      return orderPayload
    })

    payload.Orders = ordersPayload

    try {
      sendMessage(JSON.stringify(payload))
      clOrdIds.forEach((clOrdId) => {
        const timeoutId = setTimeout(() => {
          requestTimeoutsRef.current.delete(clOrdId)
          const timedOutInfo = requestsByIdRef.current.get(clOrdId)
          timedOutInfo?.onSettled?.()
          if (timedOutInfo?.isListPrimary) {
            addSnackbar.error({ message: t('errors.Something went wrong') })
          }
          requestsByIdRef.current.delete(clOrdId)
        }, REQUEST_TIMEOUT_MS)
        requestTimeoutsRef.current.set(clOrdId, timeoutId)
      })
    } catch {
      clOrdIds.forEach((id) => requestsByIdRef.current.delete(id))
      addSnackbar.error({ message: t('errors.Something went wrong') })
      onSettled?.()
    }

    return listId
  }

  const newOrder = (
    params: NewOrderParams,
    onSuccess?: () => void,
    onSettled?: () => void
  ): string => {
    if (readyState !== ReadyState.OPEN) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      onSettled?.()
      return ''
    }
    const id = `${ORDER_ID_PREFIX}${uuidv4()}`
    const payload: Record<string, unknown> = {
      $type: FIX_TRADING_TYPES.NEW_ORDER_SINGLE,
      Id: id,
      ClOrdID: id,
      Account: params.account,
      Symbol: params.symbol,
      Side: params.side,
      OrderQty: params.orderQty,
      OrdType: params.ordType,
    }
    if (params.positionEffect !== undefined) payload.PositionEffect = params.positionEffect

    if (params.timeInForce !== undefined) payload.TimeInForce = params.timeInForce

    if (params.expireDate !== undefined) payload.ExpireDate = params.expireDate

    if (params.positionID !== undefined) payload.PositionID = params.positionID

    applyPrice(payload, params.price, params.ordType)

    try {
      requestsByIdRef.current.set(id, {
        origin: params.isTpsl ? ORIGIN.TPSL : ORIGIN.NEW,
        onSuccess,
        onSettled,
        requiresTradeConfirmation: requiresTradeConfirmationForNewOrder(params),
      })
      sendMessage(JSON.stringify(payload))
      setRequestTimeout(id, REQUEST_TIMEOUT_MS, t('errors.Something went wrong'))
    } catch {
      requestsByIdRef.current.delete(id)
      addSnackbar.error({ message: t('errors.Something went wrong') })
    }
    return id
  }

  const replaceOrder = (
    params: OrderCancelReplaceParams,
    onSuccess?: () => void,
    onSettled?: () => void
  ): string => {
    if (readyState !== ReadyState.OPEN) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      onSettled?.()
      return ''
    }
    const id = `${ORDER_ID_PREFIX}${uuidv4()}`
    const payload: Record<string, unknown> = {
      $type: FIX_TRADING_TYPES.ORDER_CANCEL_REPLACE_REQUEST,
      Id: id,
      ClOrdID: id,
      OrigClOrdID: params.origClOrdID,
      Account: params.account,
      Symbol: params.symbol,
      Side: params.side,
      OrdType: params.ordType,
    }

    if (params.listId) payload.ListID = params.listId
    if (params.orderQty !== undefined) payload.OrderQty = params.orderQty
    if (params.positionEffect !== undefined) payload.PositionEffect = params.positionEffect
    if (params.timeInForce !== undefined) payload.TimeInForce = params.timeInForce
    if (params.expireDate !== undefined) payload.ExpireDate = params.expireDate
    if (params.positionID !== undefined) payload.PositionID = params.positionID

    applyPrice(payload, params.price, params.ordType)

    try {
      const origin = params.isTpsl
        ? ORIGIN.TPSL
        : params.isExpiryUpdate
        ? ORIGIN.EXPIRY_UPDATE
        : ORIGIN.NEW
      requestsByIdRef.current.set(id, {
        origin,
        onSuccess,
        onSettled,
      })
      sendMessage(JSON.stringify(payload))
      setRequestTimeout(id, REQUEST_TIMEOUT_MS, t('errors.Something went wrong'))
    } catch {
      requestsByIdRef.current.delete(id)
      addSnackbar.error({ message: t('errors.Something went wrong') })
    }
    return id
  }

  const closePosition = (params: ClosePositionOrderParams, context?: CloseContext): string => {
    if (readyState !== ReadyState.OPEN) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      context?.onSettled?.()
      return ''
    }
    const id = `${ORDER_ID_PREFIX}${uuidv4()}`
    const side = invertFixSide(params.side)
    const payload: Record<string, unknown> = {
      $type: FIX_TRADING_TYPES.NEW_ORDER_SINGLE,
      Id: id,
      ClOrdID: id,
      Account: params.account,
      Symbol: params.symbol,
      Side: side,
      OrderQty: params.orderQty,
      OrdType: '1',
      PositionEffect: FIX_POSITION_EFFECT.CLOSE,
      PositionID: params.positionID,
    }

    try {
      requestsByIdRef.current.set(id, {
        origin: ORIGIN.CLOSE,
        openPrice: context?.openPrice,
        currency: context?.currency,
        conversionRate: context?.conversionRate,
        requiresConversion: context?.requiresConversion,
        onSettled: context?.onSettled,
      })
      sendMessage(JSON.stringify(payload))
      setRequestTimeout(
        id,
        REQUEST_TIMEOUT_MS,
        t('Portfolio.Unable to close the trade. Please try again')
      )
    } catch {
      requestsByIdRef.current.delete(id)
      addSnackbar.error({ message: t('errors.Something went wrong') })
    }
    return id
  }

  const closeAllPositions = (params: CloseAllPositionsParams): string => {
    const { accountCode, positionIds, onSuccess, onSettled, symbol } = params
    if (readyState !== ReadyState.OPEN) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      onSettled?.()
      return ''
    }
    const id = `${ORDER_ID_PREFIX}${uuidv4()}`
    const account = `default:${accountCode}`
    const payload = symbol
      ? {
          $type: FIX_TRADING_TYPES.CLOSE_SYMBOL_ORDERS,
          Id: id,
          ClOrdID: id,
          Symbol: symbol,
          Account: account,
        }
      : {
          $type: FIX_TRADING_TYPES.CLOSE_ALL_ORDERS,
          Id: id,
          ClOrdID: id,
          Account: account,
        }

    if (closeAllStateRef.current?.timeoutId) {
      clearTimeout(closeAllStateRef.current.timeoutId)
    }

    const timeoutId = setTimeout(() => {
      if (closeAllStateRef.current) {
        closeAllStateRef.current.onSettled?.()
        closeAllStateRef.current = null
        addSnackbar.error({
          message: t('Portfolio.Unable to close all trades. Please try again'),
        })
      }
    }, REQUEST_TIMEOUT_MS)

    closeAllStateRef.current = {
      pendingPositionIds: new Set(positionIds),
      successCount: 0,
      failedCount: 0,
      onSuccess,
      onSettled,
      timeoutId,
    }

    try {
      sendMessage(JSON.stringify(payload))
    } catch {
      if (closeAllStateRef.current?.timeoutId) {
        clearTimeout(closeAllStateRef.current.timeoutId)
      }
      closeAllStateRef.current = null
      addSnackbar.error({ message: t('errors.Something went wrong') })
    }
    return id
  }

  const changePendingOrderSlTp = (
    params: ChangePendingOrderSlTpParams,
    onSuccess?: () => void,
    onSettled?: () => void
  ): string => {
    if (readyState !== ReadyState.OPEN) {
      addSnackbar.error({ message: t('errors.Something went wrong') })
      onSettled?.()
      return ''
    }

    const cancelId = `${ORDER_ID_PREFIX}${uuidv4()}`
    const listId = `${ORDER_ID_PREFIX}${uuidv4()}`
    let primaryClOrdId = ''

    const ordersPayload = params.orders.map((order, index) => {
      const clOrdId = `${ORDER_ID_PREFIX}${uuidv4()}`

      const orderPayload: Record<string, unknown> = {
        ClOrdID: clOrdId,
        Account: order.account,
        Symbol: order.symbol,
        Side: order.side,
        OrdType: order.ordType,
      }

      if (order.positionEffect === FIX_POSITION_EFFECT.CLOSE) {
        orderPayload.OrderQty = 0
      } else if (order.orderQty !== undefined) {
        orderPayload.OrderQty = order.orderQty
      }
      if (order.positionEffect !== undefined) orderPayload.PositionEffect = order.positionEffect
      if (order.timeInForce !== undefined) orderPayload.TimeInForce = order.timeInForce
      if (order.expireDate !== undefined) orderPayload.ExpireDate = order.expireDate
      if (order.positionID !== undefined) orderPayload.PositionID = order.positionID

      applyPrice(orderPayload, order.price, order.ordType)

      if (index === 0) primaryClOrdId = clOrdId

      return orderPayload
    })

    requestsByIdRef.current.set(primaryClOrdId, {
      origin: ORIGIN.TPSL,
      onSuccess,
      onSettled,
      linkedCancelId: cancelId,
    })
    requestsByIdRef.current.set(cancelId, {
      origin: ORIGIN.CHANGE_PENDING_TPSL_CANCEL,
      onSettled,
      linkedClOrdId: primaryClOrdId,
    })

    const payload = {
      $type: FIX_TRADING_TYPES.CHANGE_PENDING_ORDER_SL_TP,
      CancelRequest: {
        $type: FIX_TRADING_TYPES.ORDER_CANCEL_REQUEST,
        Id: cancelId,
        ClOrdID: cancelId,
        OrigClOrdID: params.origClOrdID,
        ListID: params.account,
        Account: params.account,
        Symbol: params.symbol,
        Side: params.side,
      },
      NewOrderList: {
        $type: FIX_TRADING_TYPES.NEW_ORDER_LIST,
        ListID: listId,
        ContingencyType: params.contingencyType,
        TotNoOrders: params.orders.length,
        NoOrders: params.orders.length,
        Orders: ordersPayload,
      },
    }

    try {
      sendMessage(JSON.stringify(payload))
      setRequestTimeout(
        cancelId,
        REQUEST_TIMEOUT_MS,
        t('Portfolio.Unable to update TP/SL. Please try again'),
        () => requestsByIdRef.current.delete(primaryClOrdId)
      )
    } catch {
      requestsByIdRef.current.delete(primaryClOrdId)
      requestsByIdRef.current.delete(cancelId)
      addSnackbar.error({ message: t('errors.Something went wrong') })
      onSettled?.()
    }

    return listId
  }

  return {
    readyState,
    cancelOrder,
    newOrder,
    newOrderList,
    changePendingOrderSlTp,
    replaceOrder,
    closePosition,
    closeAllPositions,
  }
}

type Snackbar = {
  success: (opts: { message: string }) => void
  error: (opts: { message: string }) => void
  warning: (opts: { message: string }) => void
}

const ORIGIN = {
  CANCEL_ORDER: 'cancel order',
  NEW: 'new',
  CLOSE: 'close position',
  TPSL: 'tpsl',
  EXPIRY_UPDATE: 'expiry update',
  CHANGE_PENDING_TPSL_CANCEL: 'change_pending_tpsl_cancel',
} as const

type Origin = (typeof ORIGIN)[keyof typeof ORIGIN]

type CloseContext = {
  openPrice?: number
  currency?: string
  conversionRate?: number | null
  requiresConversion?: boolean
  onSettled?: () => void
}
type OriginInfo = CloseContext & {
  origin: Origin
  onSuccess?: () => void
  onSettled?: () => void
  isListPrimary?: boolean
  suppressSuccessMessage?: boolean
  requiresTradeConfirmation?: boolean
  hasNewExecution?: boolean
  hasTradeExecution?: boolean
  linkedClOrdId?: string
  linkedCancelId?: string
  listGroupId?: string
}

const invertFixSide = (side: '1' | '2'): '1' | '2' => (side === '1' ? '2' : '1')

const applyPrice = (
  payload: Record<string, unknown>,
  price: number | null | undefined,
  ordType: string
) => {
  if (price != null) {
    payload[ordType === '3' ? 'StopPx' : 'Price'] = price
  }
}

const requiresTradeConfirmationForNewOrder = (params: {
  ordType: string
  positionEffect?: string
}): boolean => {
  return (
    params.ordType === FIX_ORDER_TYPE.MARKET && params.positionEffect !== FIX_POSITION_EFFECT.CLOSE
  )
}

type ProcessFixMessageDeps = {
  requestsById: Map<string, OriginInfo>
  addSnackbar: Snackbar
  t: (key: string) => string
  requestTimeouts?: Map<string, ReturnType<typeof setTimeout>>
  closeAllState?: CloseAllState | null
  clearCloseAllState?: () => void
}

type CloseAllState = {
  pendingPositionIds: Set<string>
  successCount: number
  failedCount: number
  onSuccess?: () => void
  onSettled?: () => void
  timeoutId: ReturnType<typeof setTimeout>
}

function isOrderNotFoundError(respObj: Record<string, unknown>): boolean {
  const text = typeof respObj.Text === 'string' ? respObj.Text : ''
  return text.includes('Can not find order')
}

function processFixMessage(data: unknown, deps: ProcessFixMessageDeps): boolean {
  const { requestsById, addSnackbar, t, requestTimeouts, closeAllState, clearCloseAllState } = deps

  if (!data || typeof data !== 'object') return false
  const obj = data as Record<string, unknown>
  const type = typeof obj.$type === 'string' ? obj.$type : undefined

  const clearRequestTimeout = (id?: string) => {
    if (!id) return
    const timeoutId = requestTimeouts?.get(id)
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    requestTimeouts?.delete(id)
  }

  if (type === 'OrderCancelReject') {
    const clOrdId = typeof obj.ClOrdID === 'string' ? obj.ClOrdID : undefined
    const originInfo = clOrdId ? requestsById.get(clOrdId) : undefined

    if (originInfo?.origin === ORIGIN.CHANGE_PENDING_TPSL_CANCEL) {
      if (originInfo.linkedClOrdId) {
        clearRequestTimeout(originInfo.linkedClOrdId)
        requestsById.delete(originInfo.linkedClOrdId)
      }
      originInfo?.onSettled?.()
      if (clOrdId) {
        requestsById.delete(clOrdId)
        clearRequestTimeout(clOrdId)
      }
      addSnackbar.error({
        message: getFixTradingErrorMessage(
          obj,
          t,
          t('Portfolio.Unable to update TP/SL. Please try again')
        ),
      })
      return true
    }

    originInfo?.onSettled?.()
    if (clOrdId) {
      requestsById.delete(clOrdId)
      clearRequestTimeout(clOrdId)
    }
    const errorMessage = isOrderNotFoundError(obj)
      ? t('Portfolio.Unable to manage this order here')
      : getFixTradingErrorMessage(
          obj,
          t,
          t('Portfolio.Unable to cancel the order. Please try again')
        )
    addSnackbar.error({ message: errorMessage })
    return true
  }

  if (type === 'OrderValidationException') {
    const clOrdId = typeof obj.ClOrdID === 'string' ? obj.ClOrdID : undefined
    const originInfo = clOrdId ? requestsById.get(clOrdId) : undefined

    const listGroupId = originInfo?.listGroupId
    if (listGroupId) {
      requestsById.forEach((info, id) => {
        if (info.listGroupId === listGroupId) {
          info.onSettled?.()
          clearRequestTimeout(id)
          requestsById.delete(id)
        }
      })
    } else {
      originInfo?.onSettled?.()
      if (clOrdId) {
        requestsById.delete(clOrdId)
        clearRequestTimeout(clOrdId)
      }
    }

    addSnackbar.error({
      message: getGenericExceptionMessage(obj, t, t('errors.Something went wrong')),
    })
    return true
  }

  if (type?.startsWith('GenericException')) {
    requestsById.forEach((originInfo, id) => {
      originInfo.onSettled?.()
      clearRequestTimeout(id)
    })
    requestsById.clear()
    addSnackbar.error({ message: t('errors.Something went wrong') })
    return true
  }

  if (type === 'ExecutionReport') {
    const execType = typeof obj.ExecType === 'string' ? (obj.ExecType as ExecType) : undefined
    const clOrdId = typeof obj.ClOrdID === 'string' ? obj.ClOrdID : undefined

    const originInfo = clOrdId ? requestsById.get(clOrdId) : undefined
    const origin = originInfo?.origin

    if (origin === ORIGIN.CANCEL_ORDER) {
      if (execType === EXEC_TYPE.CANCELLED) {
        if (!originInfo?.suppressSuccessMessage) {
          addSnackbar.success({ message: t('Portfolio.Order cancelled successfully') })
        }
        originInfo?.onSuccess?.()
        originInfo?.onSettled?.()
      } else if (execType === EXEC_TYPE.REJECTED) {
        const errorMessage = isOrderNotFoundError(obj)
          ? t('Portfolio.Unable to manage this order here')
          : getFixTradingErrorMessage(
              obj,
              t,
              t('Portfolio.Unable to cancel the order. Please try again')
            )
        addSnackbar.error({ message: errorMessage })
        originInfo?.onSettled?.()
      } else {
        return false
      }
      if (clOrdId) {
        requestsById.delete(clOrdId)
        clearRequestTimeout(clOrdId)
      }
      return true
    }

    if (origin === ORIGIN.NEW) {
      const finalizeNewRequest = () => {
        const isListPrimary = originInfo?.isListPrimary
        const shouldNotify = isListPrimary !== false

        if (shouldNotify) {
          addSnackbar.success({ message: t('OrderWidget.Order placed') })
        }

        originInfo?.onSuccess?.()

        originInfo?.onSettled?.()

        if (clOrdId) {
          requestsById.delete(clOrdId)
          clearRequestTimeout(clOrdId)
        }
      }

      if (execType === EXEC_TYPE.PENDING_REPLACE) {
        return true
      }

      if (execType === EXEC_TYPE.NEW || execType === EXEC_TYPE.REPLACE) {
        if (originInfo?.requiresTradeConfirmation && execType === EXEC_TYPE.NEW) {
          originInfo.hasNewExecution = true

          if (!originInfo.hasTradeExecution) {
            return true
          }
        }

        finalizeNewRequest()
        return true
      }

      if (execType === EXEC_TYPE.TRADE || execType === EXEC_TYPE.FILL) {
        if (!originInfo?.requiresTradeConfirmation) {
          return false
        }

        originInfo.hasTradeExecution = true

        finalizeNewRequest()
        return true
      }

      if (execType === EXEC_TYPE.REJECTED) {
        addSnackbar.error({
          message: getFixTradingErrorMessage(obj, t, t('OrderWidget.Order submit error')),
        })
        originInfo?.onSettled?.()
        if (clOrdId) {
          requestsById.delete(clOrdId)
          clearRequestTimeout(clOrdId)
        }
        return true
      }

      return false
    }

    if (origin === ORIGIN.EXPIRY_UPDATE) {
      if (execType === EXEC_TYPE.PENDING_REPLACE) {
        return true
      }

      if (execType === EXEC_TYPE.REJECTED) {
        const errorMessage = isOrderNotFoundError(obj)
          ? t('Portfolio.Unable to manage this order here')
          : getFixTradingErrorMessage(
              obj,
              t,
              t('Portfolio.Unable to update expiry date. Please try again')
            )
        addSnackbar.error({ message: errorMessage })
      } else if (execType === EXEC_TYPE.REPLACE || execType === EXEC_TYPE.NEW) {
        addSnackbar.success({ message: t('Portfolio.Expiry date updated successfully') })
        originInfo?.onSuccess?.()
      } else {
        return false
      }

      originInfo?.onSettled?.()
      if (clOrdId) {
        requestsById.delete(clOrdId)
        clearRequestTimeout(clOrdId)
      }
      return true
    }

    if (origin === ORIGIN.TPSL) {
      if (execType === EXEC_TYPE.REJECTED) {
        addSnackbar.error({
          message: getFixTradingErrorMessage(
            obj,
            t,
            t('Portfolio.Unable to update TP/SL. Please try again')
          ),
        })
      } else if (execType) {
        const hasOtherTpslRequests = Array.from(requestsById.entries()).some(
          ([id, info]) => id !== clOrdId && info.origin === ORIGIN.TPSL
        )

        if (!hasOtherTpslRequests) {
          addSnackbar.success({ message: t('Portfolio.TP/SL updated successfully') })
        }
      } else {
        return false
      }

      originInfo?.onSettled?.()
      if (clOrdId) {
        if (originInfo?.linkedCancelId) {
          clearRequestTimeout(originInfo.linkedCancelId)
          requestsById.delete(originInfo.linkedCancelId)
        }
        requestsById.delete(clOrdId)
        clearRequestTimeout(clOrdId)
      }
      return true
    }

    if (origin === ORIGIN.CLOSE) {
      if (execType === EXEC_TYPE.NEW) {
        return true
      } else if (execType === EXEC_TYPE.TRADE || execType === EXEC_TYPE.FILL) {
        addSnackbar.success({ message: buildCloseSuccessMessage(obj, originInfo, t) })
        originInfo?.onSettled?.()
        if (clOrdId) {
          requestsById.delete(clOrdId)
          clearRequestTimeout(clOrdId)
        }
        return false
      } else if (execType === EXEC_TYPE.REJECTED) {
        addSnackbar.error({
          message: getFixTradingErrorMessage(
            obj,
            t,
            t('Portfolio.Unable to close the trade. Please try again')
          ),
        })
        originInfo?.onSettled?.()
        if (clOrdId) {
          requestsById.delete(clOrdId)
          clearRequestTimeout(clOrdId)
        }
        return false
      } else {
        return false
      }
    }

    if (closeAllState && !origin) {
      const positionId = typeof obj.PositionId === 'string' ? obj.PositionId : undefined
      const positionEffect = typeof obj.PositionEffect === 'string' ? obj.PositionEffect : undefined

      if (
        positionId &&
        positionEffect === 'C' &&
        closeAllState.pendingPositionIds.has(positionId)
      ) {
        closeAllState.pendingPositionIds.delete(positionId)

        if (execType === EXEC_TYPE.REJECTED) {
          closeAllState.failedCount++
        } else {
          closeAllState.successCount++
        }

        if (closeAllState.pendingPositionIds.size === 0) {
          clearTimeout(closeAllState.timeoutId)
          const { successCount, failedCount } = closeAllState

          if (failedCount === 0) {
            addSnackbar.success({ message: t('Portfolio.All trades closed successfully') })
            closeAllState.onSuccess?.()
          } else if (successCount === 0) {
            addSnackbar.error({ message: t('Portfolio.All trades failed to close') })
          } else {
            addSnackbar.warning({ message: t('Portfolio.Partially successful') })
          }
          closeAllState.onSettled?.()
          clearCloseAllState?.()
        }
        return true
      }
    }
    return false
  }

  return false
}

function buildCloseSuccessMessage(
  report: Record<string, unknown>,
  context: CloseContext | undefined,
  t: (key: string) => string
): string {
  const lastPx = typeof report.LastPx === 'number' ? report.LastPx : undefined
  const orderQty = typeof report.OrderQty === 'number' ? report.OrderQty : undefined
  const rawSide = typeof report.Side === 'string' ? (report.Side as string) : undefined
  const fixSide = rawSide === '1' || rawSide === '2' ? rawSide : null
  const pnlSide = sideReverseMapping(fixSide, true)
  const pnl = calculatePnL({
    side: pnlSide,
    openPrice: context?.openPrice,
    quantity: orderQty,
    exitPrice: lastPx,
  })
  const messageBase = t('Portfolio.Trade closed successfully')
  const requiresConversion = context?.requiresConversion === true
  const conversionRate = context?.conversionRate

  const convertedPnl =
    pnl === null
      ? null
      : requiresConversion
      ? conversionRate == null
        ? null
        : pnl * conversionRate
      : pnl

  if (convertedPnl === null) return messageBase

  const formattedPnL = context?.currency
    ? formatSignedUsd(convertedPnl, context.currency as CurrencyType)
    : convertedPnl.toFixed(2)

  return `${messageBase} PnL: ${formattedPnL}`
}
